import React from 'react'
import moment from 'moment'

import { DataTable } from 'components/DataTable'
import type { DataTableColumn } from 'components/DataTable'
import { LabelValueList } from 'components/LabelValueList'
import type { LabelValueItem } from 'components/LabelValueList'
import type { BookingMasterData } from 'repositories/BerthApi/getBookingMasterData/type'
import { formatPhoneNumber } from 'utils/formatPhoneNumber'

import type { TenantShipment, PerformanceShipmentVisit } from '../type'
import { ShipmentStatusChip } from './ShipmentStatusChip'
import styles from './PerformanceTable.module.css'

const MAX_VISITS = 5

const DASH = '-'

const VISIT_COLUMN_LABELS = ['納品先1', '納品先2', '納品先3', '納品先4', '納品先5'] as const

const formatDatetime = (value: string | null): string =>
  value ? moment(value).format('YYYY/MM/DD HH:mm') : DASH

const resolveParamLabel = (
  params: BookingMasterData['vehicleCategories'] | BookingMasterData['packageTypes'],
  code: string | null | undefined
): string => {
  if (!code) return DASH
  return params.find((p) => String(p.parameterId) === String(code))?.parameterName ?? code
}

type VisitCellContentProps = {
  visit: PerformanceShipmentVisit | undefined
}

// 手入力なら delivery_destination、マスタ選択なら会社+倉庫+テナント名を連結して返す
const resolveVisitDestination = (visit: PerformanceShipmentVisit): string => {
  if (visit.deliveryDestination) return visit.deliveryDestination
  if (visit.deliveryTenantId !== null) {
    const name = [visit.deliveryCompanyName, visit.deliveryWarehouseName, visit.deliveryTenantName]
      .filter((part): part is string => !!part?.trim())
      .join('')
    return name || DASH
  }
  return DASH
}

const VisitCellContent: React.FC<VisitCellContentProps> = ({ visit }) => {
  if (!visit) {
    return <span className={styles.emptyValue}>{DASH}</span>
  }

  const items: LabelValueItem[] = [
    { label: '入場', value: formatDatetime(visit.checkinTime) },
    { label: '作業開始', value: formatDatetime(visit.startTime) },
    { label: '作業終了', value: formatDatetime(visit.endTime) },
    { label: '退場', value: formatDatetime(visit.checkoutTime) },
  ]

  return (
    <div className={styles.visitBlock}>
      <div className={styles.visitDestination}>{resolveVisitDestination(visit)}</div>
      <LabelValueList items={items} />
    </div>
  )
}

const getVisitSlots = (shipment: TenantShipment): Array<PerformanceShipmentVisit | undefined> => {
  const sorted = [...shipment.visits]
    .sort((a, b) => a.sequenceOrder - b.sequenceOrder)
    .slice(0, MAX_VISITS)
  return Array.from({ length: MAX_VISITS }, (_, i) => sorted[i])
}

const renderDriverCell = (
  shipment: TenantShipment,
  masterData: BookingMasterData | null
): React.ReactNode => {
  const { pickBooking } = shipment

  // 左列: ドライバー情報（Figma node 3138-5698 の順）
  const driverItems: LabelValueItem[] = [
    { label: 'ドライバー電話番号', value: formatPhoneNumber(shipment.driverPhone) },
    { label: 'ドライバー氏名', value: pickBooking?.driverName ?? DASH },
    {
      label: '予約時間',
      value:
        pickBooking && (pickBooking.startDatetime || pickBooking.endDatetime) ? (
          <span className={styles.bookingDatetime}>
            <span>{formatDatetime(pickBooking.startDatetime)}</span>
            <span>〜</span>
            <span>{formatDatetime(pickBooking.endDatetime)}</span>
          </span>
        ) : (
          DASH
        ),
    },
    { label: '予約受付番号', value: pickBooking?.bookingNo ?? DASH },
    {
      label: '車両形態',
      value: masterData
        ? resolveParamLabel(masterData.vehicleCategories, pickBooking?.vehicleType ?? null)
        : (pickBooking?.vehicleType ?? DASH),
    },
  ]

  // 右列: 荷積みグループ（荷主 / 荷物名 / 荷姿 / 荷量）
  const cargo = pickBooking?.export ?? null
  const cargoItems: LabelValueItem[] = [
    { label: '荷主', value: cargo?.goodsOwner || DASH },
    { label: '荷物名', value: cargo?.itemName ?? DASH },
    {
      label: '荷姿',
      value: masterData
        ? resolveParamLabel(masterData.packageTypes, cargo?.packageType ?? null)
        : (cargo?.packageType ?? DASH),
    },
    { label: '荷量', value: cargo?.quantity != null ? String(cargo.quantity) : DASH },
  ]

  return (
    <div className={styles.driverCell}>
      <LabelValueList items={driverItems} />
      <div className={styles.cargoGroup}>
        <p className={styles.cargoGroupTitle}>荷積み</p>
        <div className={styles.cargoGroupBody}>
          <LabelValueList items={cargoItems} labelWidth={64} />
        </div>
      </div>
    </div>
  )
}

// 行クリックは DataTable の onRowClick prop 側で処理するため、列定義側は受け取らない
const buildColumns = (masterData: BookingMasterData | null): DataTableColumn<TenantShipment>[] => [
  {
    key: 'driver',
    header: '',
    align: 'left',
    render: (shipment) => renderDriverCell(shipment, masterData),
  },
  {
    key: 'status',
    header: 'ステータス',
    align: 'center',
    // 見出し「ステータス」が折り返さないよう左右 padding を 0 にする
    headerStyle: { paddingLeft: 0, paddingRight: 0 },
    render: (shipment) => <ShipmentStatusChip status={shipment.status} />,
  },
  {
    key: 'createdAt',
    header: '配送開始日時',
    align: 'center',
    render: (shipment) => (
      <span className={styles.createdAt}>{formatDatetime(shipment.createdAt)}</span>
    ),
  },
  ...VISIT_COLUMN_LABELS.map(
    (label, idx): DataTableColumn<TenantShipment> => ({
      key: `visit-${idx + 1}`,
      header: label,
      align: 'left',
      // 納品先列の最低横幅 286px。th に min-width を当てると table-layout:auto では
      // 列全体が 286 以上になる（見出し折り返し防止・空セル列も 286 を確保）。
      // alignLeft クラスはドライバー列と共用のため、納品先列の th だけにインラインで当てる。
      headerStyle: { minWidth: 286 },
      render: (shipment) => {
        const slots = getVisitSlots(shipment)
        return <VisitCellContent visit={slots[idx]} />
      },
    })
  ),
]

type Props = {
  shipments: TenantShipment[]
  onRowClick: (shipmentId: number) => void
  maxHeight?: number | string
  masterData?: BookingMasterData | null
}

export const PerformanceTable: React.FC<Props> = ({
  shipments,
  onRowClick,
  maxHeight,
  masterData = null,
}) => {
  const columns = buildColumns(masterData)

  return (
    <DataTable
      columns={columns}
      rows={shipments}
      rowKey={(shipment) => shipment.id}
      onRowClick={(shipment) => onRowClick(shipment.id)}
      emptyMessage="該当する納品実績はありません"
      maxHeight={maxHeight}
    />
  )
}
